import type { inferRouterOutputs } from '@sweetistics/trpc-server';
import type { QueryClient } from '@tanstack/react-query';
import { __getEnsureEmbeddabilityMock, trpc } from '@/lib/trpc/client';
import { wrapTrpcMutation } from '@/lib/trpc/mutation-utils';
import type { EmbeddableStatus } from '@/lib/url-metadata/embeddability';
import type { AppRouter } from '@/types/app-router';
import type { LinkMetadata } from './types';

type EnsureEmbeddabilityResponse = inferRouterOutputs<AppRouter>['utilities']['ensureEmbeddability'];

export interface EnsureEmbeddabilityOptions {
  ensureEmbeddability: (variables: { url: string; forceRefresh?: boolean }) => Promise<EnsureEmbeddabilityResponse>;
  forceRefresh?: boolean;
  queryClient?: QueryClient;
  url: string;
}

export async function ensureEmbeddabilityClient({
  url,
  queryClient,
  forceRefresh,
  ensureEmbeddability,
}: EnsureEmbeddabilityOptions): Promise<EmbeddableStatus> {
  try {
    const ensureEmbeddabilityOverride = __getEnsureEmbeddabilityMock();
    const ensureFn = ensureEmbeddabilityOverride ?? ensureEmbeddability;
    const payload = await ensureFn({
      url,
      forceRefresh: forceRefresh ?? false,
    });

    if (queryClient) {
      queryClient.setQueryData<LinkMetadata>(['url-metadata', url], (existing) =>
        existing ? { ...existing, embeddableStatus: payload.status } : existing
      );

      if (payload.canonicalUrl && payload.canonicalUrl !== url) {
        queryClient.setQueryData<LinkMetadata>(['url-metadata', payload.canonicalUrl], (existing) =>
          existing ? { ...existing, embeddableStatus: payload.status } : existing
        );
      }
    }

    return payload.status;
  } catch {
    return 'unknown';
  }
}

export function useEnsureEmbeddabilityMutation() {
  return wrapTrpcMutation(
    trpc.utilities.ensureEmbeddability.useMutation({
      trpc: { abortOnUnmount: true },
    }),
    (response: EnsureEmbeddabilityResponse) => response
  );
}
